/**
 * ObjectiveListCard.tsx
 *
 * Renders the `objective_list` envelope data emitted by onething_list_objectives.
 * Uses OkrStatusBadge for consistent status colors + progressFillClass for
 * status-driven progress bar color (shared with KeyResultListCard).
 */

import { Flag, Inbox, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { OkrStatusBadge } from "@/components/okr/OkrStatusBadge";
import { ObjectiveDetailSheet } from "@/components/okr/ObjectiveDetailSheet";
import { okrService } from "@/services/okrService";
import { ObjectiveStatus } from "@/types";
import { progressFillClass } from "./KeyResultListCard";
import { useLazyEntityDetail } from "./useLazyEntityDetail";

/** No-op used for the sheet's mutation callbacks — this is a read-only
 * detail view from the chat sidebar, not an edit surface. */
const noop = () => {};

// ---------------------------------------------------------------------------
// Data shape for kind "objective_list"
// ---------------------------------------------------------------------------

export interface ObjectiveListRow {
  code: string;
  name: string;
  status: string;
  progress: number;
  targetProgress: number;
  ownerName?: string;
  /**
   * Optional — the BE envelope may or may not include it depending on tool
   * version. Required to open the detail sheet; rows without a pid stay
   * non-interactive (fall back to static display).
   */
  pid?: string;
}

// ---------------------------------------------------------------------------
// Single row
// ---------------------------------------------------------------------------

function ObjectiveRow({
  row,
  isLoading,
  onActivate,
}: {
  row: ObjectiveListRow;
  isLoading: boolean;
  onActivate: (pid: string) => void;
}) {
  const pct =
    row.targetProgress > 0 ? Math.round((row.progress / row.targetProgress) * 100) : 0;
  const interactive = !!row.pid;

  const activate = () => {
    if (row.pid) onActivate(row.pid);
  };

  return (
    <div
      className={`mx-1 rounded-lg px-2 py-2.5 transition-colors ${
        interactive ? "hover:bg-muted/50 cursor-pointer" : "cursor-default"
      }`}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? `Open objective ${row.code} ${row.name}` : undefined}
      onClick={interactive ? activate : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                activate();
              }
            }
          : undefined
      }
    >
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="font-mono text-[10.5px] font-semibold text-muted-foreground">
          {row.code}
        </span>
        <span className="text-[13px] font-semibold tracking-tight flex-1 min-w-0 truncate">
          {row.name}
        </span>
        {isLoading && <Loader2 className="h-3 w-3 shrink-0 animate-spin text-muted-foreground" />}
        <OkrStatusBadge status={row.status as ObjectiveStatus} size="sm" className="rounded-full" />
      </div>
      <div className="flex items-center gap-2 mt-1.5">
        <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden shadow-inner">
          <div
            className={`h-full rounded-full ${progressFillClass(row.status)}`}
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
        </div>
        <span className="text-[10.5px] font-semibold text-muted-foreground tabular-nums whitespace-nowrap">
          {row.progress}/{row.targetProgress} · {pct}%
        </span>
      </div>
      {row.ownerName && (
        <p className="text-[11px] text-muted-foreground mt-1">{row.ownerName}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

interface ObjectiveListCardProps {
  summary: string;
  rows: ObjectiveListRow[];
}

export function ObjectiveListCard({ summary, rows }: ObjectiveListCardProps) {
  const { entity, isLoading, openPid, selectedPid, close } = useLazyEntityDetail(
    "objective",
    okrService.getObjective,
  );

  return (
    <Card className="my-2 rounded-xl border-border/60 shadow-sm dark:shadow-none dark:ring-1 dark:ring-border/40 overflow-hidden">
      <CardHeader className="flex flex-row items-start gap-2.5 px-4 py-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Flag className="h-3.5 w-3.5" />
        </div>
        <p className="text-[13.5px] font-bold leading-tight tracking-tight flex-1 min-w-0">
          {summary}
        </p>
        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground">
          {rows.length}
        </span>
      </CardHeader>
      <CardContent className="px-2 pb-2 pt-0">
        {rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-8 text-center text-muted-foreground">
            <Inbox className="h-6 w-6 opacity-50" />
            <span className="text-xs">No objectives found.</span>
          </div>
        ) : (
          rows.map((row) => (
            <ObjectiveRow
              key={row.pid ?? row.code}
              row={row}
              isLoading={isLoading && !!row.pid && selectedPid === row.pid}
              onActivate={openPid}
            />
          ))
        )}
      </CardContent>
      {/* Only mount the sheet once the full entity has loaded — the sheet
          returns null for objective=null anyway, but this avoids an
          unnecessary render/query-state flash while fetching. */}
      <ObjectiveDetailSheet
        open={!!selectedPid && !!entity}
        onOpenChange={(open) => !open && close()}
        objective={entity ?? null}
        onEdit={noop}
        onDelete={noop}
        onAddKeyResult={noop}
        onEditKeyResult={noop}
        onDeleteKeyResult={noop}
      />
    </Card>
  );
}
